import React, { createContext, useContext, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { AuthService } from '../services/AuthService';
import { Telemetry } from 'app/services/telemetry/Telemetry';
import { postHog } from 'app/services/PostHogManager';
import type { TAuthContext } from 'app/types';

const AuthContext = createContext<TAuthContext | undefined>(undefined);

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const login = async () => {
    setIsLoading(true);
    try {
      await AuthService.login();
      const isAuth = await AuthService.isAuthenticated();
      setIsAuthenticated(isAuth);
    } catch (error) {
      Telemetry.error(error, { event: 'auth.login.fail', domain: 'auth' });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await AuthService.logout();
      setIsAuthenticated(false);
      Telemetry.event('auth.logout', { domain: 'auth' }, 'info');
      Telemetry.setUser(null);
      postHog.reset();
    } finally {
      setIsLoading(false);
    }
  };

  const checkAuthStatus = async (shouldSetLoading = true) => {
    if (shouldSetLoading) setIsLoading(true);
    try {
      const isAuth = await AuthService.isAuthenticated();
      setIsAuthenticated(isAuth);
    } catch (error) {
      Telemetry.error(error, { event: 'auth.status_check.fail', domain: 'auth' });

    } finally {
      if (shouldSetLoading) setIsLoading(false);
    }
  };

  React.useEffect(() => {
    checkAuthStatus();
  }, []);

  React.useEffect(() => {
    const { AppState } = require('react-native');

    const handleAppStateChange = (nextAppState: string) => {
      if (nextAppState === 'active' && isAuthenticated) {
        checkAuthStatus(false);
      }
    };

    const subscription = AppState.addEventListener('change', handleAppStateChange);

    return () => {
      subscription?.remove();
    };
  }, [isAuthenticated]);

  const refreshToken = async () => {
    try {
      const newToken = await AuthService.refreshToken();
      if (!newToken) {
        setIsAuthenticated(false);
      }
      return newToken;
    } catch (error) {
      Telemetry.error(error, { event: 'auth.token_refresh.call_fail', domain: 'auth' });
      setIsAuthenticated(false);
      await AuthService.clearToken();
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        login,
        logout,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
